import {Show} from 'platform/foundation';
import {match, Pattern} from 'ts-pattern';

import {ReactNode, useRef, useState} from 'react';
import {createPortal} from 'react-dom';

import {isNil, isNotNil} from 'ramda';

import {useGetCurrentUserInfoQuery} from '@dms/api/customer';
import {TelephonyCall} from '@dms/api/settings';
import {customerRoutes} from '@dms/routes';

import {composePath, suffixTestId, TestIdProps, useBoolean, useNavigate} from 'shared/web';

import {CALL_CONTROLS_HINT_MS} from '../../constants/CALL_CONTROLS_HINT_MS';
import {CALL_ENDED_HIDE_DELAY_MS} from '../../constants/CALL_ENDED_HIDE_DELAY_MS';
import {CALL_FAILED_HIDE_DELAY_MS} from '../../constants/CALL_FAILED_HIDE_DELAY_MS';
import {useBrowserPhone} from '../../hooks/useBrowserPhone';
import {useBrowserPhoneLifecycle} from '../../hooks/useBrowserPhoneLifecycle';
import {useCallElapsedSeconds} from '../../hooks/useCallElapsedSeconds';
import {useCallPanel} from '../../hooks/useCallPanel';
import {useCurrentTelephonyCall} from '../../hooks/useCurrentTelephonyCall';
import {useTelephonyCallAvailability} from '../../hooks/useTelephonyCallAvailability';
import {useTelephonyCallPushSync} from '../../hooks/useTelephonyCallPushSync';
import {getCallPhoneNumberLabel} from '../../utils/getCallPhoneNumberLabel';
import {
  getTelephonyCallBarPhase,
  isTelephonyCallUnsettled,
} from '../../utils/getTelephonyCallBarPhase';
import {getTelephonyCallIndicatorAppearance} from '../../utils/getTelephonyCallIndicatorAppearance';
import {TelephonyCallAfterCall} from '../TelephonyCallAfterCall/TelephonyCallAfterCall';
import {TelephonyCallPanelLive} from '../TelephonyCallPanel/components/TelephonyCallPanelLive';
import {TelephonyCallPanel} from '../TelephonyCallPanel/TelephonyCallPanel';
import {AutoHide} from './components/AutoHide';
import {ScreenPopOnAnswer} from './components/ScreenPopOnAnswer';
import {TelephonyCallBar} from './components/TelephonyCallBar';
import {TelephonyCallIndicatorChip} from './components/TelephonyCallIndicatorChip';

interface TelephonyCallIndicatorProps extends TestIdProps {
  /** Dot and icon only, label in a tooltip: for narrow top bars and the mobile navigation. */
  isCompact?: boolean;
  /** The viewer's availability chip, shown in this place whenever there is no call. */
  idleControl: ReactNode;
  /**
   * Available / Away items for the footer of the call panel during a call. `close` does nothing:
   * picking a status leaves the panel open.
   */
  renderAvailabilityMenu?: (close: VoidFunction) => ReactNode;
}

const getHideDelayMs = (call: TelephonyCall) =>
  match(call)
    .with({status: Pattern.not('ended')}, () => null)
    // The call moved on to the next ring step: nothing to tell this user, clear at once.
    .with({outcome: 'passed_on'}, () => 0)
    .with({outcome: Pattern.union('connected', 'answered')}, () => CALL_ENDED_HIDE_DELAY_MS)
    .otherwise(() => CALL_FAILED_HIDE_DELAY_MS);

/**
 * The viewer's availability chip that becomes the current call, outbound or inbound. While the
 * call is unsettled (ringing, or the press-1 steps of a mobile call) a bar at the bottom of the
 * page shows it too; once picked up the bar flies into the chip, which then holds the controls.
 * Lives in the layout, so it survives navigation; state comes from the push channel and
 * `GET /telephony/calls/current`. With telephony off for the user it is just the availability chip.
 * Once the viewer's call ended, the after-call card offers pairing it with the records opened.
 */
export function TelephonyCallIndicator(props: TelephonyCallIndicatorProps) {
  const {isAllowed} = useTelephonyCallAvailability();

  return isAllowed ? (
    <>
      <TelephonyCallIndicatorContent {...props} />
      <TelephonyCallAfterCall />
      <TelephonyCallPanel />
    </>
  ) : (
    props.idleControl
  );
}

function TelephonyCallIndicatorContent(props: TelephonyCallIndicatorProps) {
  useTelephonyCallPushSync();
  useBrowserPhoneLifecycle();

  const navigate = useNavigate();
  const {call, refetch} = useCurrentTelephonyCall();
  const {data: currentUser} = useGetCurrentUserInfoQuery();
  const {state: browserPhoneState, phone} = useBrowserPhone();
  const chipRef = useRef<HTMLSpanElement>(null);
  // The chip opens the call panel; the panel stays open while the viewer navigates.
  const {state: panel, store: panelStore} = useCallPanel();
  const [hiddenCallId, setHiddenCallId] = useState<string | null>(null);
  // The call this tab saw unsettled, and the one whose bar has landed in the chip since.
  const [unsettledCallId, setUnsettledCallId] = useState<string | null>(null);
  const [handedOverCallId, setHandedOverCallId] = useState<string | null>(null);
  const [rejectedCallId, setRejectedCallId] = useState<string | null>(null);
  // A call rejected here stays out of this tab unless the viewer answers it on the mobile after all.
  const [silencedCallId, setSilencedCallId] = useState<string | null>(null);
  const [isHintShown, showHint, hideHint] = useBoolean(false);
  const [hasShownHint, setHasShownHint] = useState(false);

  const elapsedSeconds = useCallElapsedSeconds(call?.connectedAt);

  // Remember a call seen unsettled, so its pickup gets the green hand-over; a call already
  // talking when the tab first sees it goes straight to the chip.
  if (isNotNil(call) && isTelephonyCallUnsettled(call) && call.id !== unsettledCallId) {
    setUnsettledCallId(call.id);
  }

  // Answered on the mobile after a reject here: the call is back for good, outcome included.
  if (isNotNil(call) && call.id === silencedCallId && call.status === 'in_call') {
    setSilencedCallId(null);
  }

  if (isNil(call) || call.id === hiddenCallId || call.id === silencedCallId) {
    return props.idleControl;
  }

  const appearance = getTelephonyCallIndicatorAppearance(call, currentUser?.id, elapsedSeconds);
  // The tab's own call keeps Mute and Hang up even while another call (an inbound one ringing the
  // mobile) takes over the chip.
  const browserCall = browserPhoneState.activeCall;
  const isRingingHere = browserPhoneState.ringingCallIds.includes(call.id);
  const hideDelayMs = getHideDelayMs(call);
  const barPhase = getTelephonyCallBarPhase(call, {
    handoverCallId: unsettledCallId !== handedOverCallId ? unsettledCallId : null,
    rejectedCallId,
  });
  const callerName = call.customerName ?? getCallPhoneNumberLabel(call);
  // Only the answerer reads `in_call` on an inbound call, so this is the viewer picking up.
  const screenPopCustomerId =
    barPhase === 'connected' && call.direction === 'inbound' ? call.customerId : null;

  const handleHide = () => {
    setHiddenCallId(call.id);

    // `/calls/current` serves only the newest live call, so an inbound call hid an outbound one
    // that may still be running; read it again to bring that one back.
    if (call.direction === 'inbound') {
      refetch();
    }
  };

  const handleHandedOver = () => {
    setHandedOverCallId(call.id);

    if (!hasShownHint) {
      setHasShownHint(true);
      showHint();
    }
  };

  // Rejecting drops only this tab's ring; the mobile may ring on, so the tab forgets the call.
  const handleReject = () => {
    panelStore.close();
    phone.reject(call.id);
    setRejectedCallId(call.id);
  };

  const handleRejectedShown = () => {
    setRejectedCallId(null);
    setSilencedCallId(call.id);
  };

  const handleOpenCustomer = (customerId: string) =>
    navigate(composePath(customerRoutes.detail, {params: {id: customerId}}));

  const handleCreateCustomer = () => navigate(customerRoutes.create);

  const handleFindCustomer = () => navigate(customerRoutes.overview);

  const handleTogglePanel = (isFromKeyboard: boolean) => {
    if (panel.isOpen) {
      panelStore.close();

      return;
    }

    hideHint();
    panelStore.open({
      isFocusRequested: isFromKeyboard,
      focusReturn: chipRef.current?.querySelector<HTMLElement>('[role="button"]') ?? null,
    });
  };

  return (
    <>
      <Show when={hideDelayMs}>
        {(delayMs) => (
          <AutoHide
            key={`${call.id}-${call.status}-${call.outcome}`}
            delayMs={delayMs}
            onElapsed={handleHide}
          />
        )}
      </Show>
      <Show when={isHintShown}>
        <AutoHide delayMs={CALL_CONTROLS_HINT_MS} onElapsed={hideHint} />
      </Show>
      <Show when={screenPopCustomerId}>
        {(customerId) => (
          <ScreenPopOnAnswer key={call.id} customerId={customerId} callerName={callerName} />
        )}
      </Show>
      <Show when={barPhase}>
        {(phase) => (
          <TelephonyCallBar
            key={`${call.id}-${phase}`}
            call={call}
            phase={phase}
            isRingingHere={isRingingHere}
            elapsedSeconds={elapsedSeconds}
            chipRef={chipRef}
            onAccept={() => phone.accept(call.id)}
            onReject={handleReject}
            canCancel={browserCall?.callId === call.id}
            onCancel={phone.hangUp}
            onOpenCustomer={handleOpenCustomer}
            onCreateCustomer={handleCreateCustomer}
            onFindCustomer={handleFindCustomer}
            onHandedOver={handleHandedOver}
            onRejectedShown={handleRejectedShown}
            data-testid={suffixTestId('telephonyCallIndicator', props)}
          />
        )}
      </Show>
      {hideDelayMs === 0 ? (
        props.idleControl
      ) : (
        <>
          <TelephonyCallIndicatorChip
            appearance={appearance}
            isCompact={props.isCompact ?? false}
            isOpen={panel.isOpen}
            isArriving={call.id === handedOverCallId}
            isHintShown={isHintShown}
            frameRef={chipRef}
            onToggle={handleTogglePanel}
            data-testid={suffixTestId('telephonyCallIndicator', props)}
          />
          {/* An ended call shows in the panel's after-call part instead. */}
          <Show when={panel.isOpen && call.status !== 'ended' ? panel.slot : null}>
            {(slot) =>
              createPortal(
                <TelephonyCallPanelLive
                  call={call}
                  appearance={appearance}
                  isRingingHere={isRingingHere}
                  browserCall={browserCall}
                  onAccept={() => phone.accept(call.id)}
                  onReject={handleReject}
                  onToggleMute={phone.toggleMute}
                  onHangUp={phone.hangUp}
                  onOpenCustomer={handleOpenCustomer}
                  onCreateCustomer={handleCreateCustomer}
                  onFindCustomer={handleFindCustomer}
                  availabilityMenu={props.renderAvailabilityMenu?.(() => undefined)}
                  data-testid={suffixTestId('telephonyCallIndicator', props)}
                />,
                slot
              )
            }
          </Show>
        </>
      )}
    </>
  );
}
